import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/smooth";

const KEY = "pause_banner_dismissed";

function fmtRange(start, end) {
  const opts = { day: "numeric", month: "short" };
  try {
    const s = new Date(start).toLocaleDateString(undefined, opts);
    if (!end || end === start) return s;
    const e = new Date(end).toLocaleDateString(undefined, opts);
    return `${s} — ${e}`;
  } catch {
    return start;
  }
}

export const EventBanner = () => {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/events/next")
      .then((res) => {
        if (!mounted) return;
        const ev = res.data;
        if (!ev || !ev.id) return;
        const dismissed = localStorage.getItem(KEY);
        if (dismissed === ev.id) return;
        setEvent(ev);
        setVisible(true);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const h = visible ? "44px" : "0px";
    document.documentElement.style.setProperty("--banner-h", h);
    return () => {
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [visible]);

  const dismiss = () => {
    if (event?.id) localStorage.setItem(KEY, event.id);
    setVisible(false);
  };

  const goToEvents = (e) => {
    e.preventDefault();
    scrollToId("#events");
  };

  return (
    <AnimatePresence>
      {visible && event && (
        <motion.div
          data-testid="event-banner"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-0 inset-x-0 z-[70] h-[44px] bg-[#F5C800] text-[#1A1A1A] flex items-center"
        >
          <a
            href="#events"
            onClick={goToEvents}
            data-testid="event-banner-cta"
            className="flex-1 h-full flex items-center justify-center gap-2 md:gap-3 px-10 md:px-6 group"
          >
            <span className="font-body text-[10px] md:text-xs uppercase tracking-[0.22em] font-medium bg-[#1A1A1A] text-[#F5C800] px-2 py-0.5 rounded-full">
              Next
            </span>
            <span className="font-body text-xs md:text-sm truncate">
              <span className="font-medium">{event.title}</span>
              <span className="mx-2 text-[#1A1A1A]/50">·</span>
              <span>{fmtRange(event.date_start, event.date_end)}</span>
              <span className="hidden md:inline">
                <span className="mx-2 text-[#1A1A1A]/50">·</span>
                {event.location}
              </span>
            </span>
            <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <button
            onClick={dismiss}
            data-testid="event-banner-close"
            aria-label="Dismiss"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-[#1A1A1A]/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
